import { lastActivityAt, matchesStatusFilter } from '@/utils/statusTransitions';
import React, { useMemo, useState } from 'react';
import { format } from 'date-fns';
import { Card, CardContent } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '@/components/ui/table';
import { Users, AlertTriangle, ChevronUp, ChevronDown, ChevronsUpDown } from 'lucide-react';
import { cn } from '@/lib/utils';
import { getDisplayStatus, type Client } from '@/types/shelter';
import { getStatusColor, getStatusLabel, resolveStatusKey } from '@/utils/statusTransitions';

interface ClientsListModeProps {
  clients: Client[];
  isLoading: boolean;
  search: string;
  statusFilter: string;
  onSelectClient?: (client: Client) => void;
}

type SortKey = 'name' | 'alerts' | 'status' | 'room' | 'caseWorker' | 'lastActivity';
type SortDir = 'asc' | 'desc';

const getStatusBadge = (client: Client) => {
  const display = getDisplayStatus(client);
  const key = display.isOnPass ? 'pass' : resolveStatusKey(client as any);
  return (
    <Badge className={getStatusColor(key, 'soft')}>
      {getStatusLabel(key)}
    </Badge>
  );
};

const getStatusLabelFor = (client: Client) => {
  const display = getDisplayStatus(client);
  const key = display.isOnPass ? 'pass' : resolveStatusKey(client as any);
  return getStatusLabel(key);
};

// Natural sort helper: splits "15a" -> [15, "a"] for proper ordering
const naturalRoomKey = (room?: string): [number, string] => {
  if (!room) return [Number.POSITIVE_INFINITY, ''];
  const m = String(room).match(/^(\d+)?\s*(.*)$/);
  const num = m?.[1] ? parseInt(m[1], 10) : Number.POSITIVE_INFINITY;
  const rest = (m?.[2] || '').toLowerCase();
  return [num, rest];
};

const compareRoom = (a?: string, b?: string) => {
  const [an, as] = naturalRoomKey(a);
  const [bn, bs] = naturalRoomKey(b);
  if (an !== bn) return an - bn;
  return as.localeCompare(bs);
};

const getSortValue = (client: Client, key: SortKey): { empty: boolean; value: any } => {
  switch (key) {
    case 'name': {
      const v = (client.name || '').toLowerCase();
      return { empty: !v, value: v };
    }
    case 'alerts': {
      const v = client.alerts?.length ?? 0;
      return { empty: false, value: v };
    }
    case 'status': {
      const v = getStatusLabelFor(client).toLowerCase();
      return { empty: !v, value: v };
    }
    case 'room': {
      return { empty: !client.room, value: client.room };
    }
    case 'caseWorker': {
      const v = (client.caseWorker || '').toLowerCase();
      return { empty: !v, value: v };
    }
    case 'lastActivity': {
      const v = client.checkInTime ? new Date(client.checkInTime).getTime() : NaN;
      return { empty: !client.checkInTime || isNaN(v), value: v };
    }
  }
};

interface SortableHeadProps {
  label: string;
  sortKey: SortKey;
  sort: { key: SortKey; dir: SortDir } | null;
  onToggle: (key: SortKey) => void;
  className?: string;
}

const SortableHead: React.FC<SortableHeadProps> = ({ label, sortKey, sort, onToggle, className }) => {
  const active = sort?.key === sortKey;
  const Icon = active ? (sort!.dir === 'asc' ? ChevronUp : ChevronDown) : ChevronsUpDown;
  return (
    <TableHead className={className}>
      <button
        type="button"
        onClick={() => onToggle(sortKey)}
        className="group inline-flex items-center gap-1 select-none hover:text-foreground transition-colors"
      >
        <span>{label}</span>
        <Icon
          className={cn(
            'h-3.5 w-3.5 transition-opacity',
            active ? 'opacity-100' : 'opacity-30 group-hover:opacity-70'
          )}
        />
      </button>
    </TableHead>
  );
};

export const ClientsListMode: React.FC<ClientsListModeProps> = ({
  clients, isLoading, search, statusFilter, onSelectClient,
}) => {
  const [sort, setSort] = useState<{ key: SortKey; dir: SortDir } | null>(null);

  const toggleSort = (key: SortKey) => {
    setSort((prev) => {
      if (!prev || prev.key !== key) return { key, dir: key === 'lastActivity' ? 'desc' : 'asc' };
      // Always flip between ascending and descending — never clears
      return { key, dir: prev.dir === 'asc' ? 'desc' : 'asc' };
    });
  };

  const displayData = useMemo(() => {
    const q = search.trim().toLowerCase();
    const filtered = clients.filter(c => {
      const fullName = (c.name || '').toLowerCase();
      const matchesSearch = !q || fullName.includes(q);
      const matchesStatus = matchesStatusFilter(c as any, statusFilter);
      return matchesSearch && matchesStatus;
    });

    if (!sort) return [...filtered].sort((a, b) => lastActivityAt(b as any) - lastActivityAt(a as any));

    const dirMul = sort.dir === 'asc' ? 1 : -1;
    return [...filtered].sort((a, b) => {
      const av = getSortValue(a, sort.key);
      const bv = getSortValue(b, sort.key);
      // empties always last
      if (av.empty && !bv.empty) return 1;
      if (!av.empty && bv.empty) return -1;
      if (av.empty && bv.empty) return 0;

      let cmp = 0;
      if (sort.key === 'room') {
        cmp = compareRoom(av.value, bv.value);
      } else if (typeof av.value === 'number' && typeof bv.value === 'number') {
        cmp = av.value - bv.value;
      } else {
        cmp = String(av.value).localeCompare(String(bv.value));
      }
      return cmp * dirMul;
    });
  }, [clients, search, statusFilter, sort]);

  return (
    <Card>
      <CardContent className="p-0">
        {isLoading ? (
          <p className="text-muted-foreground text-center py-8">Loading...</p>
        ) : displayData.length === 0 ? (
          <div className="text-center py-12">
            <Users className="h-12 w-12 mx-auto text-muted-foreground mb-4" />
            <p className="text-lg font-medium text-muted-foreground">No clients found</p>
            <p className="text-sm text-muted-foreground mt-1">
              {search ? 'Try adjusting your search or filters' : 'Add a client to get started'}
            </p>
          </div>
        ) : (
          <div className="rounded-md">
            <Table>
              <TableHeader>
                <TableRow>
                  <SortableHead label="Name" sortKey="name" sort={sort} onToggle={toggleSort} />
                  <SortableHead label="Alerts" sortKey="alerts" sort={sort} onToggle={toggleSort} className="w-12 text-center" />
                  <SortableHead label="Status" sortKey="status" sort={sort} onToggle={toggleSort} />
                  <SortableHead label="Room" sortKey="room" sort={sort} onToggle={toggleSort} />
                  <SortableHead label="Case Manager" sortKey="caseWorker" sort={sort} onToggle={toggleSort} />
                  <SortableHead label="Last Activity" sortKey="lastActivity" sort={sort} onToggle={toggleSort} />
                </TableRow>
              </TableHeader>
              <TableBody>
                {displayData.map((client) => {
                  const lastActivity = client.checkInTime
                    ? format(new Date(client.checkInTime), 'MMM d, h:mm a')
                    : '—';
                  const alertCount = client.alerts?.length ?? 0;
                  return (
                    <TableRow
                      key={client.id}
                      className="cursor-pointer hover:bg-muted/50"
                      onClick={() => onSelectClient?.(client)}
                    >
                      <TableCell>
                        <p className="font-medium text-primary hover:underline">{client.name}</p>
                      </TableCell>
                      <TableCell className="text-center">
                        {alertCount > 0 ? (
                          <span
                            className="inline-flex items-center gap-1 text-destructive"
                            title={client.alerts?.join(', ')}
                          >
                            <AlertTriangle className="h-4 w-4" />
                            <span className="text-xs font-semibold">{alertCount}</span>
                          </span>
                        ) : (
                          <span className="text-muted-foreground">—</span>
                        )}
                      </TableCell>
                      <TableCell>{getStatusBadge(client)}</TableCell>
                      <TableCell className="text-muted-foreground">{client.room || '—'}</TableCell>
                      <TableCell className="text-muted-foreground">{client.caseWorker || '—'}</TableCell>
                      <TableCell className="text-muted-foreground">{lastActivity}</TableCell>
                    </TableRow>
                  );
                })}
              </TableBody>
            </Table>
          </div>
        )}
      </CardContent>
    </Card>
  );
};
